import { CopyOutlined, DollarOutlined, ExportOutlined, GiftOutlined, SendOutlined } from '@ant-design/icons';
import { App, Button, Empty, Modal, Skeleton } from 'antd';
import { formatCredits } from '../../../data/home';
import { AppApc } from '../../../api/appApc';
import { loadRedPacketLogRows } from '../../Activity/RedPacketLogs';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18n from '../../../i18n';
import { formatDateTime as formatLocalizedDateTime, formatNumber } from '../../../i18n/format';

type ActivityRowsProps = {
    limit?: number;
    filter?: 'all' | 'purchases' | 'deposits' | 'withdraw' | 'cashback';
};

type DetailField = {
    key: string;
    label: string;
    value: string;
    copyText: string;
    mono: boolean;
    copyable: boolean;
    badge?: string;
};

const HIDDEN_KEYS = new Set([
    'failurereason',
    'logindex',
    'blocknumber',
    'requiredconfirmations',
    'type',
]);

function getStatusTone(status: unknown) {
    switch (String(status).toLowerCase()) {
        case 'credited':
        case 'earned':
        case 'confirmed':
        case 'completed':
        case 'success':
        case 'created':
        case 'allocated':
        case 'claimed':
            return 'success';
        case 'detected':
        case 'pending':
            return 'warning';
        case 'confirming':
        case 'processing':
        case 'payout_sent':
            return 'processing';
        case 'failed':
        case 'rejected':
        case 'cancelled':
        case 'create_rejected':
        case 'create_failed':
        case 'payout_failed':
            return 'danger';
        default:
            return 'neutral';
    }
}

function formatActivityDateTime(value: unknown) {
    const date = typeof value === 'number' ? new Date(value) : new Date(String(value));
    if (Number.isNaN(date.getTime())) return String(value ?? '');

    return formatLocalizedDateTime(date, {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

function getActivityDate(item: Record<string, unknown>) {
    return (
        item.detectedAt ||
        item.createdAt ||
        item.orderedAt ||
        item.purchasedAt ||
        item.earnedAt ||
        item.creditedAt ||
        item.updatedAt ||
        ''
    );
}

function getActivityAmount(item: Record<string, unknown>) {
    const value =
        item.customerTotalUsdt ??
        item.totalUsdt ??
        item.totalAmount ??
        item.amount ??
        item.earnedCashbackCredit ??
        item.faceValue ??
        (isPlainObject(item.denomination) ? item.denomination.value : undefined);
    const amount = Number(value);
    return Number.isFinite(amount) ? amount : 0;
}

function getPurchaseBrand(item: Record<string, unknown>) {
    const product = isPlainObject(item.product) ? item.product : {};
    return String(
        item.brandName ||
            item.productName ||
            product.nameEn ||
            product.name ||
            item.brand ||
            i18n.t('fallback.voucher', { ns: 'activity' })
    );
}

function getActivityAsset(item: Record<string, unknown>) {
    if (item.type === 'deposit' || item.type === 'withdraw') return String(item.asset || 'USDT');
    return i18n.t('units.credit', {
        ns: 'activity',
        count: Math.abs(getActivityAmount(item)),
    });
}

function getActivityId(item: Record<string, unknown>, index: number) {
    return String(item.id || item.orderId || item.voucherId || `${item.type}-${index}`);
}

function getItemStatus(item: Record<string, unknown>) {
    return item.status ?? item.state ?? '';
}

function redPacketEventLabel(item: Record<string, unknown>) {
    return i18n.t(`redPacket.events.${String(getItemStatus(item))}`, { ns: 'activity' });
}

function redPacketMessageLabel(item: Record<string, unknown>) {
    const text = typeof item.message === 'string' ? item.message.trim() : '';
    return text || redPacketEventLabel(item);
}

export function getActivityStatus(item: any) {
    const isDeposit = item.type === 'deposit';
    const isPending = isDeposit && item.status === 'Pending';
    const isProcessing = item.status === 'Processing';

    return {
        isDeposit,
        isPending,
        isProcessing,
        amount: formatCredits(Math.abs(item.amount)),
        prefix: isDeposit && !isPending ? '+' : item.amount < 0 ? '−' : '',
        detail: isDeposit
            ? `${item.date} · ${formatCredits(item.amount)} USDT ${
                  isPending
                      ? i18n.t('legacy.transferDetected', { ns: 'activity' })
                      : i18n.t('legacy.received', { ns: 'activity' })
              }`
            : item.date,
        statusLabel: isPending
            ? i18n.t('legacy.awaitingVerification', { ns: 'activity' })
            : isProcessing
              ? i18n.t('legacy.processingReserved', { ns: 'activity' })
              : isDeposit
                ? i18n.t('legacy.addedRate', { ns: 'activity' })
                : item.status,
        title: isDeposit
            ? i18n.t('row.usdtDeposit', { ns: 'activity' })
            : i18n.t('row.giftCard', { ns: 'activity', brand: item.brand }),
    };
}

const PREFERRED_KEYS = [
    'id',
    'status',
    'state',
    'amount',
    'fee',
    'net',
    'earnedCashbackCredit',
    'quotedCashbackMaxCredit',
    'asset',
    'network',
    'chain',
    'txHash',
    'payoutTxHash',
    'feeTxHash',
    'transactionHash',
    'hash',
    'from',
    'to',
    'fromAddress',
    'toAddress',
    'address',
    'orderId',
    'campaignId',
    'confirmations',
    'detectedAt',
    'confirmedAt',
    'earnedAt',
    'creditedAt',
    'failureReason',
    'createdAt',
    'updatedAt',
];

function isPlainObject(value: unknown): value is Record<string, unknown> {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function normalizeKey(key: string) {
    return key.replace(/[_-]/g, '').toLowerCase();
}

function isHiddenKey(key: string) {
    return HIDDEN_KEYS.has(normalizeKey(key.split('.').pop() || key));
}

function shortenAddress(address: string, start = 10, end = 8) {
    if (address.length <= start + end + 3) return address;
    return `${address.slice(0, start)}...${address.slice(-end)}`;
}

function isAddressField(key: string, value: string) {
    const leaf = normalizeKey(key.split('.').pop() || key);
    if (leaf === 'from' || leaf === 'to' || leaf.includes('address')) return value.length > 16;
    return /^0x[a-fA-F0-9]{20,}$/.test(value);
}

function formatLabel(path: string) {
    const fallback = path
        .split('.')
        .map((part) =>
            part
                .replace(/[_-]+/g, ' ')
                .replace(/([a-z\d])([A-Z])/g, '$1 $2')
                .replace(/\b\w/g, (char) => char.toUpperCase())
        )
        .join(' · ');
    return i18n.t(`details.fields.${leafKey(path)}`, {
        ns: 'activity',
        defaultValue: fallback,
    });
}

function leafKey(key: string) {
    return normalizeKey(key.split('.').pop() || key);
}

function isAmountKey(key: string) {
    const leaf = leafKey(key);
    return leaf === 'fee' || leaf === 'net' || leaf.includes('amount') || leaf.includes('cashbackcredit');
}

function isStatusKey(key: string) {
    const leaf = leafKey(key);
    return leaf === 'status' || leaf === 'state';
}

function formatAmount(value: unknown) {
    const numeric = typeof value === 'number' ? value : Number(String(value).trim());
    if (!Number.isFinite(numeric)) return String(value ?? '—');

    return formatNumber(numeric, { maximumFractionDigits: 6 });
}

function formatLeaf(key: string, value: unknown) {
    if (value == null || value === '') return '—';
    if (typeof value === 'boolean') {
        return value
            ? i18n.t('values.yes', { ns: 'activity' })
            : i18n.t('values.no', { ns: 'activity' });
    }
    if (isAmountKey(key) && (typeof value === 'number' || typeof value === 'string')) {
        return formatAmount(value);
    }
    if (typeof value === 'number') return String(value);
    if (typeof value === 'string' && (/(at|date|time)$/i.test(key) || /^\d{4}-\d{2}-\d{2}T/.test(value))) {
        return formatActivityDateTime(value);
    }
    return String(value);
}

function toDetailField(path: string, key: string, value: unknown, text: string): DetailField {
    if (isStatusKey(key)) {
        return {
            key: path,
            label: formatLabel(path),
            value: text,
            copyText: text,
            mono: false,
            copyable: false,
            badge: getStatusTone(value),
        };
    }

    const fullText = typeof value === 'string' ? value : text;
    const shortened = typeof value === 'string' && isAddressField(key, value);
    const longText = !text.includes('\n') && fullText.length > 24;

    return {
        key: path,
        label: formatLabel(path),
        value: shortened ? shortenAddress(value) : text,
        copyText: fullText,
        mono: shortened || longText,
        copyable: shortened || longText,
    };
}

function collectFields(
    source: Record<string, unknown>,
    prefix = '',
    includeFailureReason = false
): DetailField[] {
    return Object.entries(source).flatMap(([key, value]) => {
        if (leafKey(key) === 'failurereason') {
            if (!includeFailureReason || value == null || value === '') return [];
        } else if (isHiddenKey(key)) {
            return [];
        }

        const path = prefix ? `${prefix}.${key}` : key;

        if (isPlainObject(value)) {
            return collectFields(value, path, includeFailureReason);
        }

        if (Array.isArray(value)) {
            const text = value.every(
                (entry) => entry == null || ['string', 'number', 'boolean'].includes(typeof entry)
            )
                ? value.length
                    ? value.map((entry) => formatLeaf(key, entry)).join(', ')
                    : '—'
                : JSON.stringify(value, null, 2);

            return [toDetailField(path, key, text.includes('\n') ? text : value, text)];
        }

        return [toDetailField(path, key, value, formatLeaf(key, value))];
    });
}

function toActivityItems(payload: unknown): Record<string, unknown>[] {
    if (Array.isArray(payload)) {
        return payload.filter(isPlainObject);
    }

    if (!isPlainObject(payload)) return [];

    const nested = payload.items ?? payload.list ?? payload.records ?? payload.withdraws ?? payload.data;
    if (Array.isArray(nested)) {
        return nested.filter(isPlainObject);
    }
    if (isPlainObject(nested)) {
        return toActivityItems(nested);
    }

    if (payload.id || payload.amount || payload.toAddress || payload.status) {
        return [payload];
    }

    return [];
}

function readActivityItems(body: unknown) {
    if (!isPlainObject(body)) return toActivityItems(body);

    const inner = toActivityItems(body.data);
    return inner.length ? inner : toActivityItems(body);
}

function orderFields(fields: DetailField[]) {
    const rank = (key: string) => {
        const leaf = key.split('.').pop() || key;
        const index = PREFERRED_KEYS.indexOf(leaf);
        return index === -1 ? PREFERRED_KEYS.length : index;
    };

    return [...fields].sort((left, right) => rank(left.key) - rank(right.key));
}

function ActivityRows({ limit, filter = 'all' }: ActivityRowsProps) {
    const { t } = useTranslation('activity');
    const { message } = App.useApp();
    const [data, setData] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selected, setSelected] = useState<Record<string, unknown> | null>(null);
    const detailFields = selected
        ? orderFields(collectFields(selected, '', selected.type === 'withdraw')).filter(
              (field) =>
                  selected.type === 'deposit' ||
                  normalizeKey(field.key.split('.').pop() || field.key) !== 'gastxhash'
          )
          .map((field) =>
              selected.type === 'redPacket' && isStatusKey(field.key)
                  ? { ...field, value: redPacketEventLabel(selected) }
                  : field
          )
        : [];

    const copyValue = async (label: string, value: string) => {
        try {
            await navigator.clipboard.writeText(value);
            message.success(t('messages.copied', { label }));
        } catch {
            message.error(t('messages.copyFailed'));
        }
    };

    const getActivityList = async () => {
        try {
            const [depositResult, purchaseResult, cashbackResult, withdrawResult, redPacketResult] =
                await Promise.allSettled([
                    AppApc.GetApcListTransaction(),
                    AppApc.GetlistOrders(),
                    AppApc.GetCashbackAwards(),
                    AppApc.ListWithdrawHistory(),
                    loadRedPacketLogRows(),
                ]);
            const deposits =
                depositResult.status === 'fulfilled' &&
                Array.isArray(depositResult.value.data?.data)
                    ? depositResult.value.data.data.map((item: Record<string, unknown>) => ({
                          ...item,
                          type: 'deposit',
                      }))
                    : [];
            const purchases =
                purchaseResult.status === 'fulfilled' &&
                Array.isArray(purchaseResult.value.data?.data)
                    ? purchaseResult.value.data.data.map((item: Record<string, unknown>) => ({
                          ...item,
                          type: 'purchase',
                      }))
                    : [];
            const cashbacks =
                cashbackResult.status === 'fulfilled' &&
                Array.isArray(cashbackResult.value.data?.data)
                    ? cashbackResult.value.data.data.map((item: Record<string, unknown>) => ({
                          ...item,
                          type: 'cashback',
                      }))
                    : [];
            const withdraws =
                withdrawResult.status === 'fulfilled'
                    ? readActivityItems(withdrawResult.value.data).map((item) => ({
                          ...item,
                          type: 'withdraw',
                      }))
                    : [];
            const redPackets =
                redPacketResult.status === 'fulfilled'
                    ? redPacketResult.value.map((row) => {
                          const item: Record<string, unknown> = {
                              id: `${row.kind}-${row.log.id}`,
                              type: 'redPacket',
                              createdAt: row.log.createdAt,
                              amount: row.log.amount,
                              status: row.log.event,
                              email: row.log.email,
                              packetId: row.log.packetId,
                          };
                          if (row.log.message) item.message = row.log.message;
                          if (row.kind === 'claim') {
                              if (row.log.claimId) item.claimId = row.log.claimId;
                              if (row.log.payoutTxHash) item.payoutTxHash = row.log.payoutTxHash;
                          }
                          return item;
                      })
                    : [];

            setData(
                [...deposits, ...purchases, ...cashbacks, ...withdraws, ...redPackets].sort(
                    (left, right) =>
                        new Date(String(getActivityDate(right))).getTime() -
                        new Date(String(getActivityDate(left))).getTime()
                )
            );
        } catch (error) {
            console.error('Error fetching activity', error);
            setData([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        getActivityList();
    }, []);

    const filteredData = data.filter((item) => {
        if (filter === 'purchases') return item.type === 'purchase';
        if (filter === 'deposits') return item.type === 'deposit';
        if (filter === 'withdraw') return item.type === 'withdraw';
        if (filter === 'cashback') return item.type === 'cashback';
        return true;
    });

    if (isLoading) {
        return (
            <div className="activity-list" aria-busy="true" aria-label={t('loading')}>
                {Array.from({ length: Math.min(limit ?? 3, 3) }).map((_, index) => (
                    <div className="activity-row activity-row--skeleton" key={index}>
                        <Skeleton.Avatar active size={42} />
                        <span className="activity-main">
                            <Skeleton.Input active size="small" />
                            <Skeleton.Input active size="small" />
                        </span>
                        <Skeleton.Input active size="small" />
                    </div>
                ))}
            </div>
        );
    }

    if (!filteredData.length) {
        return (
            <div className="activity-empty">
                <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('empty')} />
            </div>
        );
    }

    const selectedAmount = selected ? formatCredits(getActivityAmount(selected)) : '';
    const selectedAsset = selected ? getActivityAsset(selected) : '';
    const selectedIsDeposit = selected?.type === 'deposit';
    const selectedIsWithdraw = selected?.type === 'withdraw';
    const selectedIsCashback = selected?.type === 'cashback';
    const selectedIsRedPacket = selected?.type === 'redPacket';
    const selectedStatus = selected ? getItemStatus(selected) : '';

    return (
        <>
            <div className="activity-list">
            {filteredData.slice(0, limit).map((item: Record<string, unknown>, index) => {
                const isDeposit = item.type === 'deposit';
                const isWithdraw = item.type === 'withdraw';
                const isCashback = item.type === 'cashback';
                const isRedPacket = item.type === 'redPacket';
                const status = getItemStatus(item);
                const isIncoming =
                    isDeposit || isCashback || (isRedPacket && (status === 'CLAIMED' || status === 'CREDITED'));
                const formattedAmount = formatCredits(getActivityAmount(item));
                const asset = getActivityAsset(item);
                const statusTone = getStatusTone(status);
                const brand = isDeposit
                    ? t('row.usdtDeposit')
                    : isWithdraw
                      ? t('row.usdtWithdraw')
                      : isCashback
                        ? t('row.cashbackReward')
                        : isRedPacket
                          ? redPacketEventLabel(item)
                          : t('row.giftCard', { brand: getPurchaseBrand(item) });
                const iconKind = isDeposit
                    ? 'deposit'
                    : isWithdraw
                      ? 'withdraw'
                      : isCashback
                        ? 'cashback'
                        : isRedPacket
                          ? 'red-packet'
                          : 'purchase';
                const amountPrefix = isIncoming ? '+' : isRedPacket && status !== 'CREATED' ? '' : '−';

                return (
                    <button
                        type="button"
                        className="activity-row"
                        key={getActivityId(item, index)}
                        onClick={() => setSelected(item)}
                    >
                        <span className={`brand-icon ${iconKind}`}>
                            {isDeposit ? (
                                <SendOutlined />
                            ) : isWithdraw ? (
                                <ExportOutlined />
                            ) : isCashback ? (
                                <DollarOutlined />
                            ) : (
                                <GiftOutlined />
                            )}
                        </span>
                        <span className="activity-main">
                            <strong>{brand}</strong>
                            <small>{formatActivityDateTime(getActivityDate(item))}</small>
                        </span>
                        <span
                            className={`activity-amount${isIncoming ? ' positive' : ''}`}
                        >
                            {amountPrefix}{formattedAmount} {asset}
                            <small className={`activity-status ${statusTone}`}>
                                {isRedPacket
                                    ? redPacketMessageLabel(item)
                                    : String(status || t('fallback.unknown'))}
                            </small>
                        </span>
                    </button>
                );
            })}
            </div>

            <Modal
                open={Boolean(selected)}
                onCancel={() => setSelected(null)}
                footer={null}
                centered
                className="topup-modal activity-detail-modal"
                title={
                    selectedIsDeposit
                        ? t('details.depositTitle', {
                              amount: selectedAmount,
                              asset: selectedAsset,
                          })
                        : selectedIsWithdraw
                          ? t('details.withdrawTitle', {
                                amount: selectedAmount,
                                asset: selectedAsset,
                            })
                          : selectedIsCashback
                            ? t('details.cashbackTitle')
                            : selectedIsRedPacket
                              ? t('redPacket.detailsTitle')
                            : selected
                              ? t('details.purchaseTitle', { brand: getPurchaseBrand(selected) })
                              : t('details.title')
                }
            >
                {selected ? (
                    <div className="activity-detail">
                        <div className="activity-detail__summary">
                            <span className={`activity-status ${getStatusTone(selectedStatus)}`}>
                                {selectedIsRedPacket
                                    ? redPacketMessageLabel(selected)
                                    : String(selectedStatus || t('fallback.unknown'))}
                            </span>
                            {getActivityDate(selected) ? (
                                <small>{formatActivityDateTime(getActivityDate(selected))}</small>
                            ) : null}
                        </div>
                        <div className="activity-detail__list">
                            {detailFields.map((field) => (
                                <div className="activity-detail__row" key={field.key}>
                                    <span>{field.label}</span>
                                    {field.badge ? (
                                        <span className={`activity-status ${field.badge}`}>{field.value}</span>
                                    ) : field.value.includes('\n') ? (
                                        <pre className="activity-detail__json">{field.value}</pre>
                                    ) : (
                                        <span
                                            className={`activity-detail__value${field.mono ? ' activity-detail__value--mono' : ''}`}
                                        >
                                            {field.value}
                                            {field.copyable ? (
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    className="activity-detail__copy"
                                                    icon={<CopyOutlined />}
                                                    aria-label={t('details.copyField', {
                                                        label: field.label,
                                                    })}
                                                    onClick={() => copyValue(field.label, field.copyText)}
                                                />
                                            ) : null}
                                        </span>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                ) : null}
            </Modal>
        </>
    );
}

export default ActivityRows;
